import React, { useEffect } from "react";

import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";

import {
    getReactSelectTheme,
    getReactSelectStyling,
} from "../../utils/ReactSelectStyles";
import ValidationError from "../Shared/ValidationError";
import SwitchButton from "../Buttons/SwitchButton";
import SwitchButtonComponent from "../Switches/SwitchButton";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import VerticalCheckBoxes from "../VerticalCheckBoxes";
import { isEmpty } from "lodash";
import EquationBuilderWidget from "../Widgets/EquationBuilderWidget";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import InformationTooltip from "../Tooltips/InformationTooltip";
import { DATA_TYPES, FIELD_TYPES } from "../../utils/constants";
import { includesProtocol } from "../../utils/ExternalLinksHelper";
import { toBemCase } from "../../utils/Helpers";

const UploadModelCard = ({
    widgetRef,
    options,
    formData,
    setFormData,
    error,
    isFormSubmitted,
    setIsVisible,
    isVisible,
    // modelId,
    isEquationValid,
    setIsEquationValid,
    formattedEquation,
    setFormattedEquation,
    externalVariables,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        setDefaults();
    }, []);

    const setDefaults = () => {
        options.map((option) => {
            option.default &&
                setFormData((prev) => ({
                    ...prev,
                    [option.name]: option.default,
                }));
        });
    };

    const handleInputChange = (e) => {
        if (e.target.name === "operationsFile" && isEmpty(e.target.value)) {
            handleFileClick(e.target.id);
            setFormData({
                ...formData,
                optionsFile: { name: "", data: null },
            });
        }
        if (e.target.files && e.target.files[0]) {
            let reader = new FileReader();
            const file = e.target.files[0];
            const fieldName = e.target.name;

            reader.onload = function (event) {
                setFormData({
                    ...formData,
                    [fieldName]: { name: file.name, data: event.target.result },
                });
            };

            reader.readAsDataURL(file);
            handleFileClick(e.target.id);
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value,
            });
        }
    };

    const handleSelectChange = (val, action) => {
        setFormData({
            ...formData,
            [action.name]: val,
        });
    };

    const handleCheckBoxChange = (e) => {
        setFormData((prevData) => ({
            ...prevData,
            [e.target.name]: {
                ...prevData[e.target.name],
                booleanValue: e.target.checked,
            },
        }));
    };

    const handleCheckBoxinput = (e) => {
        setFormData((prevData) => ({
            ...prevData,
            [e.target.name]: {
                ...prevData[e.target.name],
                inputValue: e.target.value,
            },
        }));
    };

    const handleSwitchChange = () => {
        setIsVisible((prev) => !prev);
    };

    const renderGridTextFields = (option) => {
        return (
            <div className="main-customer-form-inputs grid-2-cols-small mb-2">
                {option.fields?.map((field, index) => {
                    const id =
                        field.id || `${toBemCase(field.name)}-grid-text-input`;
                    return (
                        <div
                            key={index}
                            className={`input-group m-0 ${
                                error[field.name] && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <div className="flex items-center gap-x-1 mb-1">
                                <label
                                    htmlFor={id}
                                    className={`uppercase mb-0 ${
                                        field.required ? "required" : ""
                                    }`}
                                >
                                    {field.label}
                                </label>
                                {field.tooltip && (
                                    <InformationTooltip
                                        message={field.tooltip}
                                        className="fa-xs"
                                    />
                                )}
                            </div>

                            <div className="flex flex-row">
                                {stateMachine({ ...field, id }, index)}
                            </div>
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error[field.name]}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // state machine logic on options prop
    const stateMachine = (option, index) => {
        switch (option.type) {
            case FIELD_TYPES.GridTextFields:
                return renderGridTextFields(option);
            case FIELD_TYPES.TextArea:
                return (
                    <>
                        <textarea
                            placeholder={option.placeholder}
                            id={
                                option.id ||
                                `${toBemCase(option.name)}-text-area-input`
                            }
                            className="input-element textarea-element"
                            name={option.name}
                            onChange={handleInputChange}
                            value={
                                typeof formData[option.name] !== "string"
                                    ? JSON.stringify(formData[option.name])
                                    : formData[option.name]
                            }
                        />
                    </>
                );
            case FIELD_TYPES.TextBox:
                return (
                    <>
                        <input
                            type={option.dataType || "text"}
                            placeholder={option.placeholder}
                            id={
                                option.id ||
                                `${toBemCase(option.name)}-text-box-input`
                            }
                            className="input-element"
                            name={option.name}
                            onChange={handleInputChange}
                            value={formData[option.name] ?? ""}
                            min={1}
                            max={option.max || null}
                        />
                    </>
                );
            case FIELD_TYPES.Switch:
                return (
                    <SwitchButtonComponent
                        label={option.label}
                        checked={isVisible}
                        tooltip={option.tooltip}
                        onChange={handleSwitchChange}
                    />
                );
            case FIELD_TYPES.File:
                return (
                    <>
                        <div className="file-upload-wrapper">
                            <input
                                type="file"
                                className="file-upload-input"
                                placeholder={option.placeholder}
                                id={
                                    option.id ||
                                    `${toBemCase(option.name)}-file-input`
                                }
                                aria-label="File browser"
                                name={option.name}
                                onChange={handleInputChange}
                                accept={option.accept}
                                title={t("uploadFile")}
                            />
                            <label
                                className="file-upload-label"
                                htmlFor={option.id || `file-input-${index}`}
                            >
                                {renderFilePlaceholder(option)}
                            </label>
                        </div>
                    </>
                );
            case FIELD_TYPES.Dropdown:
                return (
                    <>
                        <Select
                            onChange={(val, action) =>
                                handleSelectChange(val, action)
                            }
                            isMulti={option.isMulti}
                            isClearable={false}
                            // className="input-element"
                            isSearchable
                            options={option.options}
                            placeholder={`Select ${option.label}...`}
                            name={option.name}
                            theme={getReactSelectTheme}
                            value={option.default || formData[option.name]}
                            styles={getReactSelectStyling({
                                showError: error[option.name],
                                isMultiSelect: option.isMulti,
                            })}
                        />
                    </>
                );
            case FIELD_TYPES.RadioButton:
                return (
                    <>
                        <div className="flex flex-col">
                            <VerticalRadioButtons
                                name={option.name}
                                options={option.options}
                                onChangeHandler={(e) => {
                                    handleInputChange(e);
                                }}
                                checkedReference={formData[option.name]}
                            />
                        </div>
                    </>
                );
            case FIELD_TYPES.CheckBoxes:
                return (
                    <>
                        <div className="flex flex-col">
                            <VerticalCheckBoxes
                                name={option.name}
                                options={option.options}
                                onChangeHandler={(e) => {
                                    handleCheckBoxChange(e);
                                }}
                                inputChangeHandler={(e) => {
                                    handleCheckBoxinput(e);
                                }}
                                checkedReference={formData[option.name]}
                                formData={formData}
                                includeInput={true}
                                inputLabel={t("threshold")}
                                inputPlaceHolder={t("thresholdPlaceholder")}
                                inputDefault={4}
                            />
                        </div>
                    </>
                );
            case FIELD_TYPES.EquationBuilder:
                return (
                    <EquationBuilderWidget
                        ref={widgetRef}
                        isFormSubmitted={isFormSubmitted}
                        equation={formattedEquation.value}
                        isEquationValid={isEquationValid}
                        setIsEquationValid={setIsEquationValid}
                        setFormattedEquation={setFormattedEquation}
                        variables={externalVariables}
                        isEmptyVariables={isEmpty(externalVariables)}
                    />
                );
            default:
                return;
        }
    };

    const renderFilePlaceholder = (option) => {
        const optionExtension =
            option.name === "operationsFile" ? "xml" : "pdf";
        return typeof formData[option.name] === DATA_TYPES.string
            ? `${option.label}.${optionExtension}`
            : formData[option.name]?.name || t("filePlaceholder");
    };

    const handleFileClick = (id) => {
        const element = document.getElementById(id);
        element.value = "";
    };

    const fileHandler = (field) => {
        if (
            field.type === FIELD_TYPES.File &&
            !isEmpty(formData[field.name]?.data)
        ) {
            return (
                <div className="flex">
                    <span
                        onClick={() =>
                            setFormData({
                                ...formData,
                                [field.name]: null,
                            })
                        }
                        className="hyperlink text-xs cursor-pointer"
                    >
                        <Trans i18nKey="reset">Reset</Trans>
                    </span>
                    {formData[field.name]?.data &&
                        includesProtocol(formData[field.name]?.data) && (
                            <span
                                onClick={() => {
                                    handleExternalLink(
                                        formData[field.name].data
                                    );
                                }}
                                className="hyperlink text-xs cursor-pointer ml-2"
                            >
                                <Trans i18nKey="openFile">Open file</Trans>
                                <i className="fas fa-external-link-alt fa-xs ml-1" />
                            </span>
                        )}
                </div>
            );
        }
        return null;
    };

    const renderOptions = () => {
        return (
            options &&
            options.map((option, index) => {
                if (option.show) {
                    return (
                        <div
                            key={index}
                            className={`input-group ${
                                error[option.name] && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            {option.label && (
                                <div className="flex justify-between items-center">
                                    {!(option.type === FIELD_TYPES.Switch) && (
                                        <div className="flex justify-between items-center gap-x-1 mb-1">
                                            <label
                                                htmlFor={`number-input-${index}`}
                                                className={`uppercase mb-0 ${
                                                    option.required
                                                        ? "required"
                                                        : ""
                                                }`}
                                            >
                                                {option.label}
                                            </label>
                                            {option.tooltip && (
                                                <InformationTooltip
                                                    message={option.tooltip}
                                                    className="fa-xs"
                                                />
                                            )}
                                        </div>
                                    )}
                                    {option.setIsActive && (
                                        <SwitchButton
                                            firstButton={t("jsonText")}
                                            secondButton={t("uploadFile")}
                                            isActive={option.isActive}
                                            setIsActive={option.setIsActive}
                                            entity={option.label}
                                            small
                                        />
                                    )}
                                    {fileHandler(option)}
                                </div>
                            )}
                            {option.element && option.element()}
                            {option.horizontalLine && <hr className="mb-3" />}

                            {stateMachine(option, index)}
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error[option.name]}
                                />
                            )}
                            {isFormSubmitted && option.customValidationErrors}
                        </div>
                    );
                }
            })
        );
    };
    return (
        <>
            <div className="card-table shadow mb-5">
                <div className="main-customer-form-inputs w-full-mobile">
                    {renderOptions()}
                </div>
            </div>
        </>
    );
};

export default UploadModelCard;
